import { useState, useCallback } from 'react';
import { useToast } from '@/hooks/useToast';

interface NIP05Result {
  pubkey: string;
  relays?: string[];
}

export function useNIP05() {
  const [isResolving, setIsResolving] = useState(false);
  const { toast } = useToast();

  const resolveNIP05 = useCallback(async (identifier: string): Promise<NIP05Result | null> => {
    if (!identifier.includes('@')) {
      // Already a pubkey or npub
      return null;
    }

    setIsResolving(true);
    try {
      const [name, domain] = identifier.split('@');
      if (!name || !domain) {
        throw new Error('Invalid NIP-05 identifier');
      }

      const url = `https://${domain}/.well-known/nostr.json?name=${encodeURIComponent(name)}`;
      const res = await fetch(url, { signal: AbortSignal.timeout(10000) });

      if (!res.ok) {
        throw new Error(`Failed to resolve NIP-05: HTTP ${res.status}`);
      }

      const data = await res.json();
      const pubkey = data.names?.[name];

      if (!pubkey) {
        throw new Error('Name not found in NIP-05 response');
      }

      return {
        pubkey,
        relays: data.relays?.[pubkey] || [],
      };
    } catch (err) {
      console.error('NIP-05 resolution failed:', err);
      toast({
        title: 'NIP-05 resolution failed',
        description: (err as Error).message,
        variant: 'destructive',
      });
      return null;
    } finally {
      setIsResolving(false);
    }
  }, [toast]);

  return { resolveNIP05, isResolving };
}
